"use client";

import { useEffect, useRef, type ReactElement } from "react";

type Effectiveness = "super" | "not_very" | "immune" | "normal";

type DamageProps = {
  amount: number;
  effectiveness?: Effectiveness;
  critical?: boolean;
  /**
   * Absolute pixel position within the (position: relative/absolute) parent.
   * Provide x/y directly, or use percentage strings for resolution-independent
   * placement (e.g. left: "82%"). When both x/y are given they win.
   */
  x: number | string;
  y: number | string;
  onComplete?: () => void;
};

type BannerProps = {
  text: string;
  x: number;
  y: number;
  durationMs?: number;
  onComplete?: () => void;
};

const DAMAGE_DURATION_MS = 1500;
const BANNER_FADE_MS = 200;

const effectivenessLabel: Record<Effectiveness, string | null> = {
  super: "Super effective!",
  not_very: "Not very effective...",
  immune: "No effect!",
  normal: null,
};

// Color-coded per Surggies palette: green super-effective, grey not-very,
// white normal/immune. Crit overrides to red (handled below).
const effectivenessColor: Record<Effectiveness, string> = {
  super: "#5fcf4a",
  not_very: "#9a948a",
  immune: "#f5ecd6",
  normal: "#ffffff",
};

const CRIT_COLOR = "#e34b3a";

export function DamageNumber({
  amount,
  effectiveness = "normal",
  critical = false,
  x,
  y,
  onComplete,
}: DamageProps): ReactElement {
  const firedRef = useRef(false);

  useEffect(() => {
    if (!onComplete) return;
    const id = window.setTimeout(() => {
      if (firedRef.current) return;
      firedRef.current = true;
      onComplete();
    }, DAMAGE_DURATION_MS);
    return () => window.clearTimeout(id);
  }, [onComplete]);

  const label = amount === 0 && effectiveness === "immune" ? "0!" : `-${Math.abs(amount)}`;
  const baseFontSize = critical ? 21 : 14; // 1.5× when critical
  const effLabel = effectivenessLabel[effectiveness];
  const effColor = effectivenessColor[effectiveness];
  // Crit always reads red; otherwise the number takes its effectiveness color.
  const numberColor = critical ? CRIT_COLOR : effColor;

  // 1px black outline via text-shadow
  const blackOutline =
    "1px 0 0 #000, -1px 0 0 #000, 0 1px 0 #000, 0 -1px 0 #000, " +
    "1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000";

  const critOutline = critical
    ? "1px 0 0 #b89968, -1px 0 0 #b89968, 0 1px 0 #b89968, 0 -1px 0 #b89968, " +
      "1px 1px 0 #b89968, -1px -1px 0 #b89968, 1px -1px 0 #b89968, -1px 1px 0 #b89968"
    : blackOutline;

  const animName = `surggies-damage-float-${critical ? "crit" : "normal"}`;

  return (
    <span
      aria-hidden
      style={{
        position: "absolute",
        left: x,
        top: y,
        transform: "translate(-50%, -50%)",
        transformOrigin: "center",
        pointerEvents: "none",
        fontFamily: "var(--font-geist-mono), ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
        lineHeight: 1,
        letterSpacing: "0.05em",
        imageRendering: "pixelated",
        WebkitFontSmoothing: "none",
        MozOsxFontSmoothing: "grayscale",
        userSelect: "none",
        whiteSpace: "nowrap",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "2px",
        animation: `${animName} ${DAMAGE_DURATION_MS}ms steps(12, end) forwards`,
        willChange: "transform, opacity",
      }}
      onAnimationEnd={() => {
        if (firedRef.current) return;
        firedRef.current = true;
        onComplete?.();
      }}
    >
      <style>{`
        @keyframes ${animName} {
          0%   { transform: translate(-50%, -50%); opacity: 1; }
          60%  { opacity: 1; }
          100% { transform: translate(-50%, calc(-50% - 48px)); opacity: 0; }
        }
      `}</style>

      {/* CRITICAL! label above the number */}
      {critical && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: "#b89968",
            textShadow: blackOutline,
          }}
        >
          CRITICAL!
        </span>
      )}

      {/* Main damage number */}
      <span
        style={{
          fontSize: baseFontSize,
          fontWeight: 700,
          color: numberColor,
          textShadow: critOutline,
        }}
      >
        {label}
      </span>

      {/* Effectiveness label below the number */}
      {effLabel && (
        <span
          style={{
            fontSize: 9,
            fontWeight: 500,
            color: effColor,
            textShadow: blackOutline,
          }}
        >
          {effLabel}
        </span>
      )}
    </span>
  );
}

export function BattleBanner({
  text,
  x,
  y,
  durationMs = 1200,
  onComplete,
}: BannerProps): ReactElement {
  const firedRef = useRef(false);
  const safeDuration = Math.max(durationMs, BANNER_FADE_MS * 2);
  const fadeInPct = (BANNER_FADE_MS / safeDuration) * 100;
  const fadeOutPct = 100 - fadeInPct;

  useEffect(() => {
    if (!onComplete) return;
    const id = window.setTimeout(() => {
      if (firedRef.current) return;
      firedRef.current = true;
      onComplete();
    }, safeDuration);
    return () => window.clearTimeout(id);
  }, [onComplete, safeDuration]);

  const pixelFontStack =
    '"Press Start 2P", "VT323", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        left: x,
        top: y,
        transform: "translate(-50%, -50%)",
        pointerEvents: "none",
        fontFamily: pixelFontStack,
        fontSize: 14,
        lineHeight: 1.2,
        letterSpacing: "0.05em",
        color: "#1a1a1a",
        fontWeight: 700,
        backgroundColor: "#f4ead5",
        border: "2px solid #1a1a1a",
        boxShadow: "0 2px 0 #1a1a1a",
        padding: "8px 14px",
        textAlign: "center",
        imageRendering: "pixelated",
        WebkitFontSmoothing: "none",
        MozOsxFontSmoothing: "grayscale",
        userSelect: "none",
        whiteSpace: "nowrap",
        animation: `surggies-banner-fade ${safeDuration}ms linear forwards`,
        opacity: 0,
        willChange: "opacity",
      }}
      onAnimationEnd={() => {
        if (firedRef.current) return;
        firedRef.current = true;
        onComplete?.();
      }}
    >
      <style>{`
        @keyframes surggies-banner-fade {
          0% { opacity: 0; }
          ${fadeInPct.toFixed(3)}% { opacity: 1; }
          ${fadeOutPct.toFixed(3)}% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
      {text}
    </div>
  );
}
